import { createTheme } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

import { borderRadius, color, lineHeight, maxWidth, shadows, space, typography } from "./designTokens";

const lightTheme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: color.primary,
    },
    secondary: {
      main: color.secondary,
    },
    background: {
      default: color.background,
    },
    text: {
      primary: color.typographyBase,
      secondary: color.typographyBase,
    },
  },
  typography: {
    fontFamily: typography.fontFamily,
    body2: {
      fontSize: typography.fontSize.md,
    },
  },
});

const listTheme = {
  components: {
    RaList: {
      styleOverrides: {
        root: {
          boxShadow: shadows.none,
          minWidth: 0,
          "& .RaList-main": { minWidth: 0 },
          "& .MuiPaper-root": {
            boxShadow: shadows.none,
            borderRadius: borderRadius.md,
            overflow: "hidden",
          },
          "& .RaList-content": {
            borderRadius: borderRadius.md,
            minWidth: 0,
          },
          "& .RaList-actions": {
            paddingTop: space.sm,
            alignItems: "center",
            flexWrap: "wrap",
            [lightTheme.breakpoints.down("sm")]: {
              alignItems: "stretch",
              flexWrap: "nowrap",
            },
          },
          "& .RaFilterForm-root": {
            paddingTop: space.xs,
            paddingBottom: space.xs,
            alignItems: "center",
          },
        },
      },
    },
  },
};

const menuTheme = {
  components: {
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: typography.fontSize.sm,
        },
      },
    },
    RaMenuItemLink: {
      styleOverrides: {
        root: {
          color: color.typographyBase,
          fontSize: typography.fontSize.md,
          "& .MuiListItemIcon-root": {
            minWidth: space.xxl2,
          },
          "& .MuiSvgIcon-root": {
            fontSize: typography.fontSize.lg,
          },
        },
      },
    },
  },
};

const datagridTheme = {
  components: {
    RaDatagrid: {
      styleOverrides: {
        root: {
          backgroundColor: color.background,
          minWidth: 0,
          maxWidth: "100%",
          "& .MuiTable-root": {
            width: "100%",
            minWidth: "800px", // Minimum width to prevent excessive squashing
          },
          // Add horizontal scroll for smaller screens
          overflowX: "auto",
          "& .MuiTableHead-root": {
            marginBottom: space.sm, // 8px
            backgroundColor: color.datagridHeaderBackground,
            borderRadius: borderRadius.md,
            boxShadow: shadows.none,
            position: "relative",
            // Add pseudo gap below header
            "&::after": {
              content: '""',
              display: "block",
              height: space.md, // Gap size
              backgroundColor: color.background,
              width: "100%",
            },
            "& .MuiTableRow-root": {
              height: space.xxxl,
              backgroundColor: "transparent",
              "& .MuiTableCell-root:first-child": {
                borderBottomLeftRadius: borderRadius.md, // 8px - only bottom left
              },
              "& .MuiTableCell-root:last-child": {
                borderBottomRightRadius: borderRadius.md, // 8px - only bottom right
              },
            },
            "& .MuiTableCell-root": {
              fontSize: typography.fontSize.md,
              fontWeight: typography.fontWeight.semibold,
              backgroundColor: "transparent",
              border: "none",
              borderBottom: "none",
              padding: space.xl,
              height: space.xxxl,
              verticalAlign: "middle",
              wordWrap: "break-word",
              lineHeight: lineHeight.xxs,
            },
          },
          "& .MuiTableBody-root": {
            backgroundColor: "transparent",
            overflow: "hidden",
            "& .MuiTableRow-root": {
              height: space.xxxl,
              backgroundColor: color.white,
              borderBottom: `1px solid ${color.datagridBorder}`,
            },
            "& .MuiTableCell-root": {
              fontSize: typography.fontSize.md,
              fontWeight: typography.fontWeight.normal,
              border: "none",
              padding: space.xl,
              height: space.xxxl,
              verticalAlign: "middle",
              maxWidth: maxWidth.xs,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
            "& .MuiTableRow-root:first-child": {
              "& .MuiTableCell-root:first-child": {
                borderTopLeftRadius: borderRadius.md,
              },
              "& .MuiTableCell-root:last-child": {
                borderTopRightRadius: borderRadius.md,
              },
            },
          },
        },
      },
    },
  },
};

const filterTheme = {
  components: {
    RaFilterForm: {
      styleOverrides: {
        root: {
          alignItems: "center",
          minWidth: 0,
          maxWidth: "100%",
          [lightTheme.breakpoints.down("sm")]: {
            "& .RaFilterForm-filterFormInput": { width: "100%" },
            "& .MuiFormControl-root": { minWidth: 0, maxWidth: "100%", width: "100%" },
          },
          "& .RaFilterForm-filterFormInput .MuiFormControl-root": {
            marginTop: 0,
          },
          "& .RaFilterForm-filterFormInput .MuiOutlinedInput-root input": {
            padding: `${space.sm2}px ${space.xl}px`,
          },
        },
      },
    },
  },
};

const topToolbarTheme = {
  components: {
    RaTopToolbar: {
      styleOverrides: {
        root: {
          alignItems: "center",
          minWidth: 0,
          flexWrap: "wrap",
          gap: space.sm,
          [lightTheme.breakpoints.down("sm")]: {
            flex: "0 1 auto",
            justifyContent: "flex-start",
            "& .MuiButton-root": { marginLeft: 0 },
          },
          "&.MuiToolbar-dense": {
            padding: 0,
          },
        },
      },
    },
  },
};

const showTheme = {
  components: {
    RaShow: {
      styleOverrides: {
        root: {
          minWidth: 0,
          "& .RaShow-main, & .RaShow-card": { minWidth: 0 },
          "& .MuiPaper-root": {
            backgroundColor: "transparent",
            boxShadow: shadows.none,
            borderRadius: borderRadius.none,
          },
        },
      },
    },
  },
};

const tabTheme = {
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: typography.fontSize.md,
          fontWeight: typography.fontWeight.normal,
          "&.Mui-selected": {
            fontWeight: typography.fontWeight.medium,
          },
        },
      },
    },
  },
};

const tabbedShowLayoutTheme = {
  components: {
    RaTabbedShowLayout: {
      styleOverrides: {
        root: {
          "& .RaTabbedShowLayout-content": {
            paddingLeft: 0,
            paddingRight: 0,
          },
        },
      },
    },
  },
};

const buttonTheme = {
  components: {
    MuiButton: {
      styleOverrides: {
        root: () => ({
          color: color.typographyBase,
          padding: `${space.sm2}px ${space.xl}px`,
          borderRadius: borderRadius.sm,
          fontSize: typography.fontSize.md,
          fontWeight: typography.fontWeight.medium,
          textTransform: "none",
          "&:disabled": {
            backgroundColor: color.disabled,
            color: color.white,
          },
          "&.MuiButton-colorPrimary": {
            color: color.white,
            "&.MuiButton-outlined": {
              color: color.typographyBase,
            },
            "&.MuiButton-text": {
              color: color.typographyBase,
            },
          },
          "&.MuiButton-colorSecondary": {
            borderRadius: borderRadius.sm,
            background: color.secondaryButtonBackground,
            color: color.typographyBase,
            boxShadow: shadows.secondary,
            textTransform: "none",
            marginLeft: space.xl,
            padding: `${space.sm2}px ${space.xl}px`,
            "& .MuiButton-startIcon": {
              color: color.typographyBase,
              "& svg[data-testid='BlockIcon']": {
                color: color.error,
              },
              "& svg[data-testid='CheckCircleIcon']": {
                color: color.success,
              },
            },
            "&:hover": {
              background: color.secondaryButtonHover,
              color: color.typographyBase,
              boxShadow: shadows.secondaryHover,
              "& .MuiButton-startIcon": {
                "& svg[data-testid='BlockIcon']": {
                  color: color.error,
                },
                "& svg[data-testid='CheckCircleIcon']": {
                  color: color.success,
                },
              },
            },
            "&:disabled": {
              background: color.secondaryButtonDisabled,
              color: color.secondaryButtonDisabledText,
              boxShadow: shadows.none,
            },
          },
          ".MuiDialogContent-root &": {
            padding: `${space.sm2}px ${space.xl}px`,
            "&.MuiButton-colorSecondary": {
              padding: `${space.sm2}px ${space.xl}px`,
            },
          },
        }),
        contained: () => ({
          "&:hover": {
            backgroundColor: color.hover,
          },
        }),
      },
    },
    MuiTypography: {
      styleOverrides: {
        root: {
          color: color.typographyBase,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: color.typographyBase,
        },
      },
    },
  },
};

const formToolbarTheme = {
  components: {
    MuiToolbar: {
      styleOverrides: {
        root: {
          "&.RaToolbar-desktopToolbar": {
            backgroundColor: color.transparent,
            padding: `0 ${space.xxl}`,
            minHeight: "auto",
          },
          "&.MuiToolbar-gutters.RaToolbar-desktopToolbar": {
            paddingLeft: space.xxl,
            paddingRight: space.xxl,
          },
        },
      },
    },
  },
};
const formTheme = {
  components: {
    MuiDialog: {
      styleOverrides: {
        paper: {
          [lightTheme.breakpoints.down("sm")]: {
            "&:not(.MuiDialog-paperFullScreen)": {
              margin: space.sm,
              width: `calc(100% - ${space.sm * 2}px)`,
              maxHeight: `calc(100% - ${space.sm * 2}px)`,
            },
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          flexWrap: "wrap",
          gap: space.sm,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: shadows.none,
          borderRadius: borderRadius.md,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: 0,
          paddingBottom: space.xxl1,
        },
      },
    },
    MuiDialogContentText: {
      styleOverrides: {
        root: {
          // React-admin's Confirm component uses DialogContentText to render content.
          // Since MuiDialogContent has padding: 0 globally (line 373), this marginTop
          // provides necessary spacing between DialogTitle and the confirmation text.
          paddingTop: space.xl,
        },
      },
    },
  },
};

const reactAdminFormTheme = {
  components: {
    RaSimpleForm: {
      styleOverrides: {
        root: {
          padding: 0,
          margin: 0,
          paddingLeft: space.xxl,
          paddingRight: space.xxl,
          paddingTop: space.lg,
          [lightTheme.breakpoints.down("sm")]: {
            paddingLeft: space.xl,
            paddingRight: space.xl,
          },
        },
      },
    },
  },
};

export const appTheme = createTheme(
  [
    lightTheme,
    listTheme,
    menuTheme,
    datagridTheme,
    filterTheme,
    topToolbarTheme,
    showTheme,
    tabTheme,
    tabbedShowLayoutTheme,
    buttonTheme,
    formTheme,
    formToolbarTheme,
    reactAdminFormTheme,
  ].reduce((acc, theme) => deepmerge(acc, theme), {})
);
